import pluralize from 'pluralize';

import type { SetlistErrorHandler } from './client';

export interface FilterState {
  excluded: string[];
  included: string[];
}

// Motion graphics JSON query to Typesense filter query
export const parseCategoryQuery = (
  query: string,
  onError?: SetlistErrorHandler
) => {
  let obj = {};

  try {
    obj = JSON.parse(query);
  } catch {
    onError?.(
      new Error(
        'Error parsing category query for motion graphics category page'
      ),
      {
        operation: 'parse-category-query',
        query,
      }
    );

    return '';
  }

  const filter = Object.entries(obj)
    .map(([key, value]): [string, unknown[]] => [
      key.toLowerCase(),
      Array.isArray(value) ? value : [value],
    ])
    // An empty list is "no rule". Typesense rejects a clause with no values.
    .filter(([, values]) => values.length > 0)
    .map(([lowerCaseKey, values]) => {
      // Quoted one by one: an asset has to carry every keyword in the list.
      if (lowerCaseKey === 'keywords') {
        return `keywords:=[${values.map((keyword) => `"${String(keyword)}"`).join(',')}]`;
      }

      if (
        lowerCaseKey === 'type' ||
        lowerCaseKey === 'theme' ||
        lowerCaseKey === 'style'
      ) {
        return `${pluralize(lowerCaseKey)}.id:=[${String(values)}]`;
      }

      // For all other fields, search for the id field
      return `${lowerCaseKey}.id:=[${String(values)}]`;
    });

  return filter.join(' && ');
};

export const parsePreselectedFilters = (
  query: string,
  onError?: SetlistErrorHandler
) => {
  if (!query) {
    return {};
  }

  try {
    // Handle string format (e.g. "types.id:=[79]")
    const formattedObj: Record<string, FilterState> = {};
    const filterConditions = query.split(' && ');

    filterConditions.forEach((condition) => {
      const [category, valuesStr] = condition.split(':');
      const isExclude = valuesStr.startsWith('!=');
      const values = valuesStr.replace(/^(!=|=)?\[|\]$/g, '').split(',');
      const lowerCaseCategory = category.toLowerCase();

      if (!formattedObj[lowerCaseCategory]) {
        formattedObj[lowerCaseCategory] = {
          included: [],
          excluded: [],
        };
      }

      if (isExclude) {
        formattedObj[lowerCaseCategory].excluded.push(...values);
      } else {
        formattedObj[lowerCaseCategory].included.push(...values);
      }
    });

    return formattedObj;
  } catch {
    onError?.(
      new Error(
        'Error parsing preselected filters for motion graphics category page'
      ),
      {
        operation: 'parse-category-query',
        query,
      }
    );

    return {};
  }
};

/**
 * Preselected filters for an MG/LUT category page (tags/types/compatibility
 * locked by the category), derived from the category's `query_base`.
 *
 * Used by BOTH the category page components and their getServerSideProps.
 * The SSR prefetch must serialize this exact object with
 * `convertFiltersToFilterBy` — the same call the client's first render makes —
 * or the prefetched React Query cache key won't match and the grid SSRs as
 * skeletons (the July 2026 no-JS/Googlebot regression).
 */
export const getCategoryPreselectedFilters = (
  categoryItem: {
    id: number | string;
    mg_category?: string | null;
    query_base?: string;
  },
  onError?: SetlistErrorHandler
): Record<string, FilterState> => {
  const { query_base } = categoryItem;
  const hasQuery = query_base !== undefined && query_base !== '{}';

  const preSelectedFilter = hasQuery
    ? parseCategoryQuery(query_base, onError)
    : `${pluralize(categoryItem.mg_category?.toLowerCase() ?? '')}.id:=[${categoryItem.id}]`;

  return parsePreselectedFilters(preSelectedFilter, onError);
};

/**
 * LUT category pages use `is_lut:true` to scope assets. The shared
 * `query_base` on LUT category rows still includes `Type: [84]`, which
 * over-constrains the search and returns no results — drop it here.
 */
export const getLutCategoryPreselectedFilters = (
  categoryItem: {
    id: number | string;
    mg_category?: string | null;
    query_base?: string;
  },
  onError?: SetlistErrorHandler
): Record<string, FilterState> => {
  const { query_base } = categoryItem;
  const hasQuery = query_base !== undefined && query_base !== '{}';

  if (hasQuery) {
    try {
      const obj = JSON.parse(query_base) as Record<string, unknown>;
      const withoutType = Object.fromEntries(
        Object.entries(obj).filter(([key]) => key.toLowerCase() !== 'type')
      );

      if (Object.keys(withoutType).length > 0) {
        return parsePreselectedFilters(
          parseCategoryQuery(JSON.stringify(withoutType), onError),
          onError
        );
      }
    } catch {
      // Fall through to the shared parser below.
    }
  }

  const { 'types.id': _typeFilter, ...filters } = getCategoryPreselectedFilters(
    categoryItem,
    onError
  );

  return filters;
};

// Fields where several values mean "any of them". Everything else is "all".
const ANY_OF_FIELDS = new Set(['compatibility.id']);

/** `filter_by` for a category's preselected filters, as its page sends it. */
export const categoryFiltersToFilterBy = (
  filters: Record<string, FilterState>
): string =>
  Object.entries(filters)
    .flatMap(([field, { included, excluded }]) => [
      ...(included.length > 1 && ANY_OF_FIELDS.has(field)
        ? [`${field}:=[${included.join(',')}]`]
        : included.map((value) => `${field}:${value}`)),
      ...excluded.map((value) => `${field}:!=${value}`),
    ])
    .join(' && ');
